import React, { useEffect, useMemo, useState } from "react";
import HeaderNormal from "../components/HeaderNormal";
import Footer from "../components/Footer";
import "../styles/Decouvrir.css";

import { apiFetch, logout as authLogout } from "../api/auth";

import TravelerCard from "../components/travelers/TravelerCard";
import TravelerEditor from "../components/travelers/TravelerEditor";
import {
  cryptoRandomId,
  sanitizeTraveler,
  validateTraveler,
} from "../components/travelers/helpers";

const Decouvrir = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  // ✅ Admin = token + /api/auth/me
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // ✅ Editor state
  const [editingId, setEditingId] = useState(null); // null | "NEW" | _id
  const [draft, setDraft] = useState(null);

  // ✅ UX
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // -------------------------
  // 1) Check admin
  // -------------------------
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/api/auth/me", { method: "GET" });
        const data = await res.json();
        setIsAdmin(Boolean(data?.isAuth && data?.user?.role === "admin"));
      } catch {
        setIsAdmin(false);
      } finally {
        setAuthChecked(true);
      }
    })();
  }, []);

  // -------------------------
  // 2) Load travelers
  // -------------------------
  async function loadTravelers(search = "") {
    setErrorMsg("");
    setLoading(true);

    try {
      const url = search
        ? `/api/travelers?search=${encodeURIComponent(search)}`
        : "/api/travelers";

      const res = await apiFetch(url, { method: "GET" });
      const data = await res.json();

      if (!res.ok) throw new Error(data?.error || "Erreur chargement travelers");

      const list = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      setItems(list);
    } catch (e) {
      setErrorMsg(e?.message || "Erreur chargement");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTravelers();
  }, []);

  // -------------------------
  // 3) Search (client-side fallback)
  //    Si ton backend supporte ?search=, on peut aussi le faire serveur.
  // -------------------------
  const filteredItems = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return items;
    return items.filter((it) => (it.title || "").toLowerCase().includes(query));
  }, [items, q]);

  const currentEditingItem = useMemo(() => {
    if (!editingId || editingId === "NEW") return null;
    return items.find((x) => x._id === editingId) || null;
  }, [editingId, items]);

  // -------------------------
  // Actions UI
  // -------------------------
  function startCreate() {
    setEditingId("NEW");
    setDraft({
      _id: cryptoRandomId(), // le backend peut remplacer par un vrai id Mongo, c’est ok
      title: "",
      subtitle: "",
      photo: { url: "", alt: "" },
      links: { facebook: "", instagram: "", polarsteps: "" },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  }

  function startEdit(id) {
    const item = items.find((x) => x._id === id);
    if (!item) return;
    setEditingId(id);
    setDraft(JSON.parse(JSON.stringify(item)));
  }

  function cancelEdit() {
    setEditingId(null);
    setDraft(null);
  }

  function handleLogout() {
    authLogout();
    setIsAdmin(false);
    cancelEdit();
  }

  // -------------------------
  // CRUD API (Mongo)
  // -------------------------
  async function createTraveler(payload) {
    const res = await apiFetch("/api/travelers", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || "Erreur création");
    return data?.item || data;
  }

  async function updateTraveler(id, payload) {
    const res = await apiFetch(`/api/travelers/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || "Erreur modification");
    return data?.item || data;
  }

  async function deleteTraveler(id) {
    const res = await apiFetch(`/api/travelers/${id}`, { method: "DELETE" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || "Erreur suppression");
    return true;
  }

  async function onDeleteItem(id) {
    if (!window.confirm("Supprimer cette card ?")) return;
    setErrorMsg("");
    setSaving(true);

    try {
      await deleteTraveler(id);
      setItems((prev) => prev.filter((x) => x._id !== id));
      if (editingId === id) cancelEdit();
    } catch (e) {
      setErrorMsg(e?.message || "Erreur suppression");
    } finally {
      setSaving(false);
    }
  }

  async function saveDraft() {
    if (!draft) return;

    setErrorMsg("");
    setSaving(true);

    try {
      const clean = sanitizeTraveler(draft);
      const errors = validateTraveler(clean);
      if (errors.length) {
        window.alert(errors.join("\n"));
        setSaving(false);
        return;
      }

      clean.updatedAt = new Date().toISOString();
      if (!clean.createdAt) clean.createdAt = clean.updatedAt;

      if (editingId === "NEW") {
        const created = await createTraveler(clean);
        setItems((prev) => [created, ...prev]);
      } else {
        const updated = await updateTraveler(editingId, clean);
        setItems((prev) => prev.map((x) => (x._id === editingId ? updated : x)));
      }

      cancelEdit();
    } catch (e) {
      setErrorMsg(e?.message || "Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <HeaderNormal />

      {/* ✅ Tu gardes ton H1 */}
      <h1>
        Ils nous font <span>confiance</span>
      </h1>

      <main className="tw-page">
        <header className="tw-header">
          <div>
            <p className="tw-subtitle">
              Photos & retours d’expérience de nos voyageurs.
            </p>

            <div className="tw-searchRow">
              <input
                className="tw-search"
                placeholder="Rechercher par titre (ex: Durand, Corse...)"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>

            {errorMsg && <p className="tw-error">{errorMsg}</p>}
            {loading && <p className="tw-muted">Chargement…</p>}
          </div>

          {authChecked && isAdmin && (
            <div className="tw-adminActions">
              <button className="tw-btn" type="button" onClick={startCreate} disabled={saving}>
                + Ajouter
              </button>
              <button className="tw-btn tw-btnGhost" type="button" onClick={handleLogout} disabled={saving}>
                Déconnexion
              </button>
            </div>
          )}
        </header>

        {isAdmin && (editingId === "NEW" || currentEditingItem) && (
          <TravelerEditor
            editingId={editingId}
            draft={draft}
            setDraft={setDraft}
            onSave={saveDraft}
            onCancel={cancelEdit}
            saving={saving}
          />
        )}

        <section className="tw-cards">
          {!loading &&
            filteredItems.map((item) => (
              <TravelerCard
                key={item._id}
                item={item}
                isAdmin={isAdmin}
                onEdit={() => startEdit(item._id)}
                onDelete={() => onDeleteItem(item._id)}
                disabled={saving}
              />
            ))}

          {!loading && filteredItems.length === 0 && (
            <p className="tw-empty">Aucun résultat pour “{q}”.</p>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Decouvrir;
